import type { Tuning } from './tuning';

/**
 * River layout and flow field. Distance downstream is `d = -z`; the lateral
 * position is `s` in [-1, 1] across the banks (x offset from the centreline,
 * scaled by half-width). Curves are gentle enough that treating s as a pure x
 * offset reads correctly.
 */

export const RIVER_LENGTH = 292;
/** Water and banks extend this far upstream and downstream of the course. */
export const RIVER_D_MIN = -14;
export const RIVER_D_MAX = RIVER_LENGTH + 40;

type Key = readonly [d: number, value: number];

/**
 * Course sections (tense stretches, then breathers):
 *   0–18 warm-up · 18–42 slalom · 44 DROP 1 · 46–54 landing pool
 *   54–86 canyon gates · 90 DROP 2 (rock in the landing)
 *   98–128 SPLIT: calm safe channel (left) vs rocky shortcut with its own drop (right)
 *   128–132 breather · 134 DROP 3 (big) · 138–170 zig-zag
 *   174–202 LOGS drifting · 204–228 RAPIDS (standing waves) · 238 SLUICE GATE
 *   248–266 DARK PIPE under a road · 270–285 HERONS on both banks · 292 finish
 */
const HALF_WIDTH: readonly Key[] = [
  [-20, 4.2], [16, 4.2], [22, 3.6], [40, 3.6], [47, 5], [53, 5], [58, 3.1], [84, 3.1],
  [89, 3.6], [94, 4.8], [99, 6.6], [126, 6.6], [130, 4.8], [133, 3.8], [140, 4.6],
  [160, 3.4], [170, 3.8], [176, 5], [200, 5], [204, 4], [228, 4], [232, 4.6], [238, 4.6],
  [242, 4.2], [246, 2.3], [268, 2.3], [272, 4.5], [300, 4.5], [360, 4.5],
];

/** Fast-lane centre as a fraction of half-width. This is the racing line. */
const LANE: readonly Key[] = [
  [-20, 0], [12, 0], [21, -0.5], [28, 0.5], [35, -0.5], [41, 0.2], [44, 0],
  [52, 0.45], [60, -0.4], [68, 0.45], [76, -0.45], [84, 0.1], [90, 0],
  [96, 0.45], [101, 0.8], [125, 0.8], [130, 0.1], [134, 0],
  [142, 0.5], [148, -0.5], [154, 0.5], [160, -0.45], [166, 0.45], [172, 0],
  [180, 0.35], [188, -0.35], [196, 0.3], [202, 0], [228, 0], [246, 0], [268, 0],
  [272, -0.25], [276, 0], [281, 0.25], [286, 0], [360, 0],
];

/** Standing waves: bumps across the river that hop the duck. */
export const RAPIDS_START = 204;
export const RAPIDS_END = 228;
const RAPIDS_WAVELENGTH = 3.2;
const RAPIDS_HEIGHT = 0.3;
const RAPIDS_FLOW = 1.3;

/** Floating logs drift through this stretch (see Hazards). */
export const LOGS_START = 174;
export const LOGS_END = 202;

/** The sluice: a wall across the river with a lifting gate in the middle. */
export const GATE_D = 238;
export const GATE_HALF_GAP = 1.1;

/** The culvert: the river runs straight through a dark pipe under a road. */
export const TUNNEL_START = 248;
export const TUNNEL_END = 266;
export const TUNNEL_RADIUS = 2.7;

/** Herons stand on the bank lip here: [distance, side (-1 left, 1 right)]. */
export const HERONS: readonly [d: number, side: number][] = [
  [272, -1],
  [281, 1],
];

/** Waterfalls: [distance, height]. The duck launches off the lip and lands downstream. */
export const DROPS: readonly [d: number, height: number][] = [
  [44, 3],
  [90, 3.5],
  [134, 4.5],
];
const DROP_LENGTH = 0.8;
/** Gentle downhill between drops, metres of fall per metre. */
const SLOPE = 0.025;

/**
 * Rocks as [distance, metres from the fast-lane centre, radius]. Positive = right.
 * Offsets just past the radius leave a close-shave line beside the rock; 0
 * puts it in the lane and forces a choice.
 */
const ROCK_LAYOUT: readonly [d: number, offset: number, r: number][] = [
  // slalom: rocks on the outside of each weave
  [24, -1.6, 0.7], [27, 1.3, 0.6], [31, -1.4, 0.7], [34, 1.5, 0.6], [38, -1.3, 0.7],
  // landing pool: one rock to jump or dodge
  [49, 0, 0.6],
  // canyon gates: pairs either side of the lane, plus a rock on the slow side
  [59, -1.35, 0.6], [59, 1.35, 0.6], [65, 1.4, 0.65], [70, -1.3, 0.6], [70, 1.3, 0.6],
  [75, -1.45, 0.6], [80, -1.25, 0.55], [80, 1.25, 0.55],
  // drop 2 landing: a rock sitting right where you land
  [93.5, 0, 0.75],
  // shortcut chute: rocks jut from the island wall (-) and the bank (+) alternately
  [104, -1.25, 0.5], [107.5, 1.25, 0.5], [114.5, 0, 0.45], [119, 1.25, 0.5], [123, -1.25, 0.5],
  // after drop 3: the zig-zag, rocks tight on the insides
  [140, 1.3, 0.6], [145, -1.3, 0.6], [150, 1.3, 0.6], [153, -1.4, 0.55],
  [157, 1.25, 0.6], [162, -1.3, 0.6], [165, 1.35, 0.55], [170, -1.3, 0.6],
  // rapids: a couple of rocks you can hop over on a crest
  [212, 1.3, 0.5], [221, -1.3, 0.5],
];

/** Gaps between a rock and the bank are either closed or at least this wide (m). */
const MIN_GAP = 1.3;

const WHIRL_LAYOUT: readonly [d: number, s: number, radius: number][] = [
  [52, -0.6, 1.8],
];

/**
 * The split: an island divides the river between SPLIT_START and SPLIT_END.
 * The right channel is the shortcut (fast, rocky, its own small waterfall);
 * the left is calm and slower. Both fall SPLIT_DROP in total so they rejoin
 * level: the left gradually, the right all at once.
 */
export const SPLIT_START = 99;
export const SPLIT_END = 127;
const ISLAND_OFFSET = 1.2;
const ISLAND_MAX_HALF_WIDTH = 2.6;
export const SPLIT_DROP_D = 111;
const SPLIT_DROP = 1.6;
/** How much faster the calm channel runs than plain base flow. */
const SAFE_CHANNEL_BOOST = 1.45;

export type Rock = {
  d: number;
  x: number;
  z: number;
  r: number;
  /** Forced slide direction (+1 right, -1 left) when one side is too tight to pass; 0 = either. */
  slide: number;
};
export type Whirlpool = { d: number; x: number; z: number; radius: number };

export type FlowSample = {
  x: number;
  z: number;
  speed: number;
  /** 0 outside a whirlpool, up to 1 at its core. */
  whirl: number;
};

function smooth(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c);
}

function sampleKeys(keys: readonly Key[], d: number): number {
  if (d <= keys[0][0]) return keys[0][1];
  for (let i = 1; i < keys.length; i += 1) {
    const [d1, v1] = keys[i];
    if (d <= d1) {
      const [d0, v0] = keys[i - 1];
      const t = (d - d0) / (d1 - d0);
      return v0 + (v1 - v0) * t * t * (3 - 2 * t);
    }
  }
  return keys[keys.length - 1][1];
}

export class River {
  readonly rocks: Rock[];
  readonly whirlpools: Whirlpool[];

  constructor(private readonly tuning: Tuning) {
    this.rocks = ROCK_LAYOUT.map(([d, offset, r]) => {
      const hw = this.halfWidth(d);
      const cx = this.centerX(d);
      let x = this.laneX(d) + offset;
      // A gap narrower than a comfortable duck-width is a trap: close it by
      // pushing the rock against the bank instead.
      const gapLeft = x - r - (cx - hw);
      const gapRight = cx + hw - (x + r);
      if (gapLeft > 0 && gapLeft < MIN_GAP) x -= gapLeft + 0.15;
      else if (gapRight > 0 && gapRight < MIN_GAP) x += gapRight + 0.15;
      // Same rule against the island's shore in the split.
      const islandX = this.islandX(d);
      if (islandX !== null) {
        const iw = this.islandHalfWidth(d);
        const side = x >= islandX ? 1 : -1;
        const gapIsland = Math.abs(x - islandX) - r - iw;
        if (gapIsland > 0 && gapIsland < MIN_GAP) x -= side * (gapIsland + 0.15);
      }
      const nearLeft = x - r - (cx - hw) < MIN_GAP;
      const nearRight = cx + hw - (x + r) < MIN_GAP;
      const slide = nearLeft ? 1 : nearRight ? -1 : 0;
      return { d, r, x, z: -d, slide };
    });
    this.whirlpools = WHIRL_LAYOUT.map(([d, s, radius]) => ({ d, radius, x: this.xAt(d, s), z: -d }));
  }

  centerX(d: number): number {
    const curve = (v: number) => 3.5 * Math.sin(v * 0.045) + 1.6 * Math.sin(v * 0.1 + 1.1) - 1.6 * Math.sin(1.1);
    // Straighten through the pipe: a culvert is a straight pipe.
    const w = smooth((d - 238) / 8) * (1 - smooth((d - 270) / 8));
    if (w <= 0) return curve(d);
    const a = curve(240);
    const b = curve(276);
    const line = a + ((b - a) * (d - 240)) / 36;
    return curve(d) + (line - curve(d)) * w;
  }

  /** 0..1 how deep inside the pipe distance d is (ramps at the mouths). */
  tunnelAt(d: number): number {
    return smooth((d - (TUNNEL_START - 2)) / 3) * (1 - smooth((d - (TUNNEL_END - 1)) / 3));
  }

  /** 0..1 rapids envelope at d. */
  rapidsAt(d: number): number {
    return smooth((d - RAPIDS_START) / 2) * (1 - smooth((d - (RAPIDS_END - 2)) / 2));
  }

  /** 0..1 how close d is to a standing-wave crest (for foam). */
  crestAt(d: number): number {
    const env = this.rapidsAt(d);
    if (env <= 0) return 0;
    const phase = (0.5 + 0.5 * Math.sin((2 * Math.PI * (d - RAPIDS_START)) / RAPIDS_WAVELENGTH)) ** 6;
    return env * phase;
  }

  /**
   * Water surface height: a gentle downhill with waterfall steps. Inside the
   * split the two channels differ, so pass x there (defaults to the left).
   */
  heightAt(d: number, x = -Infinity): number {
    let h = -SLOPE * d;
    for (const [dropD, dropH] of DROPS) {
      const t = Math.max(0, Math.min(1, (d - dropD) / DROP_LENGTH));
      h -= dropH * t * t * (3 - 2 * t);
    }
    const rapids = this.rapidsAt(d);
    if (rapids > 0) {
      const wobble = Number.isFinite(x) ? 0.85 + 0.15 * Math.sin(x * 1.3 + d * 0.7) : 1;
      h += rapids * RAPIDS_HEIGHT * wobble * (0.5 + 0.5 * Math.sin((2 * Math.PI * (d - RAPIDS_START)) / RAPIDS_WAVELENGTH));
    }
    return h + this.splitOffset(d, x);
  }

  /** True just past a waterfall lip: launches there are big air, not wave hops. */
  nearDrop(d: number): boolean {
    return [...DROPS.map(([dropD]) => dropD), SPLIT_DROP_D].some((dropD) => d > dropD - 0.5 && d < dropD + 1.5);
  }

  private splitOffset(d: number, x: number): number {
    if (d <= SPLIT_START) return 0;
    if (d >= SPLIT_END) return -SPLIT_DROP;
    if (this.inShortcut(d, x)) {
      const t = Math.max(0, Math.min(1, (d - SPLIT_DROP_D) / DROP_LENGTH));
      return -SPLIT_DROP * t * t * (3 - 2 * t);
    }
    const t = (d - SPLIT_START) / (SPLIT_END - SPLIT_START);
    return -SPLIT_DROP * t * t * (3 - 2 * t);
  }

  /** Island centre x, or null outside the split. */
  islandX(d: number): number | null {
    if (d <= SPLIT_START || d >= SPLIT_END) return null;
    return this.centerX(d) + ISLAND_OFFSET;
  }

  /** Island half-width: a lens that is widest mid-split and 0 at the tips. */
  islandHalfWidth(d: number): number {
    if (d <= SPLIT_START || d >= SPLIT_END) return 0;
    const t = (d - SPLIT_START) / (SPLIT_END - SPLIT_START);
    return ISLAND_MAX_HALF_WIDTH * Math.pow(Math.sin(Math.PI * t), 0.55);
  }

  /** True for the right-hand (shortcut) channel of the split. */
  inShortcut(d: number, x: number): boolean {
    const ix = this.islandX(d);
    return ix !== null && x >= ix;
  }

  /** 0..1 how close (d, x) is to the foot of a waterfall (for splash foam). */
  plungeAt(d: number, x = -Infinity): number {
    let v = 0;
    const foot = (dropD: number) => {
      const after = d - dropD;
      if (after > -0.2 && after < 5) v = Math.max(v, after < DROP_LENGTH ? 1 : 1 - (after - DROP_LENGTH) / (5 - DROP_LENGTH));
    };
    for (const [dropD] of DROPS) foot(dropD);
    if (this.inShortcut(d, x)) foot(SPLIT_DROP_D);
    return v;
  }

  halfWidth(d: number): number {
    return sampleKeys(HALF_WIDTH, d);
  }

  laneX(d: number): number {
    return this.centerX(d) + sampleKeys(LANE, d) * this.halfWidth(d);
  }

  xAt(d: number, s: number): number {
    return this.centerX(d) + s * this.halfWidth(d);
  }

  /** Downstream unit direction at distance d, as [x, z]. */
  direction(d: number, out: { x: number; z: number }): { x: number; z: number } {
    const slope = this.centerX(d + 0.5) - this.centerX(d - 0.5);
    const len = Math.hypot(slope, 1);
    out.x = slope / len;
    out.z = -1 / len;
    return out;
  }

  private readonly dir = { x: 0, z: 0 };

  flowAt(x: number, z: number, out: FlowSample): FlowSample {
    const t = this.tuning;
    const d = -z;
    const hw = this.halfWidth(d);
    const s = (x - this.centerX(d)) / hw;
    // Follow the banks: water at lateral position s keeps to s as the river
    // bends and narrows, so a drifting duck isn't shoved into the bank.
    const sc = Math.max(-1, Math.min(1, s));
    const slope =
      this.centerX(d + 0.5) - this.centerX(d - 0.5) + sc * (this.halfWidth(d + 0.5) - this.halfWidth(d - 0.5));
    const len = Math.hypot(slope, 1);
    const dir = this.dir;
    dir.x = slope / len;
    dir.z = -1 / len;

    const s6 = s * s * s * s * s * s;
    const bank = Math.max(0.45, 1 - s6);
    const laneOffset = (x - this.laneX(d)) / t.laneWidth;
    const speed = t.baseFlow * bank + t.laneBoost * Math.exp(-laneOffset * laneOffset) * bank;
    let vx = dir.x * speed;
    let vz = dir.z * speed;

    const rapids = this.rapidsAt(d);
    if (rapids > 0) {
      vx *= 1 + (RAPIDS_FLOW - 1) * rapids;
      vz *= 1 + (RAPIDS_FLOW - 1) * rapids;
    }

    // The sluice wall funnels water toward its gap.
    const toGate = GATE_D - d;
    if (toGate > -0.5 && toGate < 8) {
      const gapX = this.laneX(GATE_D);
      const pull = Math.max(-1.6, Math.min(1.6, (gapX - x) * 0.7)) * (1 - Math.max(0, toGate) / 8);
      vx += -dir.z * pull;
      vz += dir.x * pull;
    }

    const islandX = this.islandX(d);
    if (islandX !== null) {
      const side = x >= islandX ? 1 : -1;
      if (side < 0) {
        // The calm channel still moves, just steadily and without a fast line.
        const ramp = Math.min(1, (d - SPLIT_START) / 4, (SPLIT_END - d) / 4);
        vx *= 1 + (SAFE_CHANNEL_BOOST - 1) * ramp;
        vz *= 1 + (SAFE_CHANNEL_BOOST - 1) * ramp;
      }
      // Water parts around the island's nose and hugs away from its sides.
      const gap = Math.abs(x - islandX) - this.islandHalfWidth(d);
      if (gap < 1.2) {
        const push = (1 - Math.max(0, gap) / 1.2) * 1.6;
        vx += -dir.z * side * push;
        vz += dir.x * side * push;
      }
    }
    // Eddies off the banks ease a drifting duck back toward open water.
    const nearBank = Math.max(0, Math.abs(s) - 0.72) / 0.28;
    if (nearBank > 0) {
      const inward = -Math.sign(s) * nearBank * 1.4;
      vx += -dir.z * inward;
      vz += dir.x * inward;
    }

    for (const rock of this.rocks) {
      if (Math.abs(rock.d - d) > rock.r * 7) continue;
      const dx = x - rock.x;
      const dz = z - rock.z;
      const dist = Math.max(0.05, Math.hypot(dx, dz));
      const nx = dx / dist;
      const nz = dz / dist;
      // Water flows around the rock instead of into it.
      const vn = vx * nx + vz * nz;
      const lateral = dx * -dir.z + dz * dir.x;
      if (vn < 0) {
        const push = Math.min(1, ((rock.r * 1.5) / dist) ** 2);
        vx -= nx * vn * push;
        vz -= nz * vn * push;
        // Slide around the rock instead of stalling on its upstream face.
        const side = rock.slide !== 0 ? rock.slide : lateral >= 0 ? 1 : -1;
        vx += -dir.z * side * -vn * push * 1.2;
        vz += dir.x * side * -vn * push * 1.2;
      }
      // Slow wake behind it: drafting a rock costs you.
      const along = dx * dir.x + dz * dir.z;
      if (along > 0) {
        const wake = this.tuning.rockWake * Math.exp(-((lateral / (rock.r * 1.1)) ** 2)) * Math.exp(-along / (rock.r * 4));
        vx *= 1 - wake;
        vz *= 1 - wake;
      }
    }

    let whirl = 0;
    for (const pool of this.whirlpools) {
      const dx = x - pool.x;
      const dz = z - pool.z;
      const dist = Math.hypot(dx, dz);
      if (dist >= pool.radius) continue;
      const k = 1 - dist / pool.radius;
      const nx = dist > 0.01 ? dx / dist : 0;
      const nz = dist > 0.01 ? dz / dist : 0;
      const hold = Math.min(1, k * 1.6);
      vx = vx * (1 - hold * 0.9) - nz * t.whirlSwirl * k - nx * t.whirlPull * Math.sqrt(k);
      vz = vz * (1 - hold * 0.9) + nx * t.whirlSwirl * k - nz * t.whirlPull * Math.sqrt(k);
      whirl = Math.max(whirl, k);
    }

    out.x = vx;
    out.z = vz;
    out.speed = Math.hypot(vx, vz);
    out.whirl = whirl;
    return out;
  }

  /** Fastest possible water speed, for normalising visuals. */
  maxSpeed(): number {
    return this.tuning.baseFlow + this.tuning.laneBoost;
  }
}
